import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, Grid, LinearProgress, Typography, Alert, Snackbar,
    FormControl, InputLabel, Select
} from "@mui/material";
import { SaveOutlined as SaveIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import AppHeader from '../../components/AppHeader';

const PayrollSettings = ({ setIsLoggedIn }) => {
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({
        PAY_CYCLE_TYPE: 'CALENDAR',
        PAY_CYCLE_START_DAY: '1',
        PAY_CYCLE_END_DAY: '0',
        SSB_DEFAULT_ENABLED: '1',
        PIT_DEFAULT_ENABLED: '1',
        SSB_BASE_MODE: 'ACTUAL_CAPPED',
        SSB_AGE_SCHEME_MODE: 'AGE_BASED',
        SSB_MISSING_DOB_POLICY: 'BLOCK',
        SSB_FIXED_ZERO_EARNINGS: '0',
        NEGATIVE_NET_PAY_POLICY: 'BLOCK'
    });
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/settings`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                const map = response.data.data.reduce((acc, s) => ({ ...acc, [s.setting_key]: s.setting_value }), {});
                setSettings(map);
            }
        } catch (error) {
            console.error("Error fetching settings:", error);
            setStatusMsg({ open: true, msg: 'Error fetching settings', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const payload = {
                settings: Object.entries(settings).map(([key, value]) => ({ key, value }))
            };
            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/settings`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Settings updated successfully', severity: 'success' });
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            setStatusMsg({ open: true, msg: 'Error saving settings', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Payroll Settings" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 3, flexGrow: 1 }}>
                <Card sx={{ maxWidth: 800, mx: 'auto', p: 4, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                        <Typography variant="h5" fontWeight="bold">Payroll Rules & Cycles</Typography>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchSettings}>Reset</Button>
                    </Stack>

                    {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight="600" color="primary" gutterBottom>Pay Cycle Configuration</Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Define how the system determines the start and end dates for monthly payroll processing.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Cycle Type</InputLabel>
                                <Select
                                    value={settings.PAY_CYCLE_TYPE}
                                    label="Cycle Type"
                                    onChange={(e) => setSettings({ ...settings, PAY_CYCLE_TYPE: e.target.value })}
                                >
                                    <MenuItem value="CALENDAR">Calendar Month (1st to End)</MenuItem>
                                    <MenuItem value="CUSTOM">Custom Cutoff (e.g. 26th to 25th)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {settings.PAY_CYCLE_TYPE === 'CUSTOM' && (
                            <>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth label="Start Day" type="number"
                                        value={settings.PAY_CYCLE_START_DAY}
                                        onChange={(e) => setSettings({ ...settings, PAY_CYCLE_START_DAY: e.target.value })}
                                        helperText="e.g. 26"
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth label="End Day" type="number"
                                        value={settings.PAY_CYCLE_END_DAY}
                                        onChange={(e) => setSettings({ ...settings, PAY_CYCLE_END_DAY: e.target.value })}
                                        helperText="e.g. 25"
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight="600" color="primary" gutterBottom>Approval Workflow</Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Choose whether one HR Manager may prepare and approve payroll, or approval must be performed by another user.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Approval Mode</InputLabel>
                                <Select
                                    value={settings.PAYROLL_APPROVAL_MODE || 'SINGLE_MANAGER'}
                                    label="Approval Mode"
                                    onChange={(e) => setSettings({ ...settings, PAYROLL_APPROVAL_MODE: e.target.value })}
                                >
                                    <MenuItem value="SINGLE_MANAGER">Single HR Manager</MenuItem>
                                    <MenuItem value="MAKER_CHECKER">Maker–Checker</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight="600" color="primary" gutterBottom>Statutory Calculations</Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Company defaults apply unless an employee has an explicit setting in Personnel Payroll.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>SSB Default</InputLabel>
                                <Select value={settings.SSB_DEFAULT_ENABLED || '1'} label="SSB Default" onChange={(e) => setSettings({ ...settings, SSB_DEFAULT_ENABLED: e.target.value })}>
                                    <MenuItem value="1">Enabled</MenuItem><MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Income Tax Default</InputLabel>
                                <Select value={settings.PIT_DEFAULT_ENABLED || '1'} label="Income Tax Default" onChange={(e) => setSettings({ ...settings, PIT_DEFAULT_ENABLED: e.target.value })}>
                                    <MenuItem value="1">Enabled</MenuItem><MenuItem value="0">Disabled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>SSB Contribution Base</InputLabel>
                                <Select value={settings.SSB_BASE_MODE || 'ACTUAL_CAPPED'} label="SSB Contribution Base" onChange={(e) => setSettings({ ...settings, SSB_BASE_MODE: e.target.value })}>
                                    <MenuItem value="ACTUAL_CAPPED">Actual Earnings, Capped at Maximum</MenuItem>
                                    <MenuItem value="GROSS_CAPPED">Gross Salary, Capped at Maximum</MenuItem>
                                    <MenuItem value="BASIC_CAPPED">Basic Salary, Capped at Maximum</MenuItem>
                                    <MenuItem value="FIXED_MAXIMUM">Fixed Maximum Every Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>SSB Age Scheme</InputLabel>
                                <Select value={settings.SSB_AGE_SCHEME_MODE || 'AGE_BASED'} label="SSB Age Scheme" onChange={(e) => setSettings({ ...settings, SSB_AGE_SCHEME_MODE: e.target.value })}>
                                    <MenuItem value="AGE_BASED">Apply Scheme by Employee Age</MenuItem>
                                    <MenuItem value="STANDARD_RATE_FOR_ALL">Standard Scheme for All Staff</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Missing Date of Birth</InputLabel>
                                <Select value={settings.SSB_MISSING_DOB_POLICY || 'BLOCK'} label="Missing Date of Birth" onChange={(e) => setSettings({ ...settings, SSB_MISSING_DOB_POLICY: e.target.value })}>
                                    <MenuItem value="BLOCK">Block Payroll Readiness</MenuItem>
                                    <MenuItem value="STANDARD_RATE">Use Standard Scheme</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Fixed SSB with Zero Earnings</InputLabel>
                                <Select value={settings.SSB_FIXED_ZERO_EARNINGS || '0'} label="Fixed SSB with Zero Earnings" onChange={(e) => setSettings({ ...settings, SSB_FIXED_ZERO_EARNINGS: e.target.value })}>
                                    <MenuItem value="0">Do Not Charge</MenuItem><MenuItem value="1">Charge Fixed Maximum</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Negative Net Pay</InputLabel>
                                <Select value={settings.NEGATIVE_NET_PAY_POLICY || 'BLOCK'} label="Negative Net Pay" onChange={(e) => setSettings({ ...settings, NEGATIVE_NET_PAY_POLICY: e.target.value })}>
                                    <MenuItem value="BLOCK">Block Calculation</MenuItem><MenuItem value="ALLOW">Allow Negative Net Pay</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} pt={4}>
                            <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                <Typography variant="body2" color="primary.dark">
                                    <strong>Pro Tip:</strong> If you use "26th to 25th", processing for <strong>February</strong> will automatically pull attendance from <strong>January 26th to February 25th</strong>.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2, textAlign: 'right' }}>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                size="large"
                                onClick={handleSave}
                                sx={{ bgcolor: '#1a237e', px: 4, borderRadius: 2 }}
                            >
                                Save Settings
                            </Button>
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={6000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
            >
                <Alert severity={statusMsg.severity} sx={{ width: '100%' }}>{statusMsg.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PayrollSettings;
